import type { FeatureVelocity, TrackedFeatureData, ProjectVelocity } from "./types.js";

/**
 * Calculate velocity metrics for a single feature from its tracked sessions.
 */
export function calculateFeatureVelocity(
  data: TrackedFeatureData,
): FeatureVelocity {
  const sessions = data.sessions;

  if (sessions.length === 0) {
    return emptyFeatureVelocity(data);
  }

  // Time metrics
  const starts = sessions.map((s) => s.sessionStart).filter(Boolean);
  const ends = sessions
    .map((s) => s.sessionEnd ?? s.sessionStart)
    .filter(Boolean);
  const firstSession = Math.min(...starts);
  const lastSession = Math.max(...ends);
  const wallClockMs = lastSession - firstSession;
  const wallClockDays = wallClockMs / (1000 * 60 * 60 * 24);

  const totalActiveMinutes = sessions.reduce(
    (sum, s) => sum + (s.activeMinutes ?? 0),
    0,
  );
  const activeHours = totalActiveMinutes / 60;

  // Token metrics
  const totalInputTokens = sessions.reduce(
    (sum, s) => sum + s.inputTokens,
    0,
  );
  const totalOutputTokens = sessions.reduce(
    (sum, s) => sum + s.outputTokens,
    0,
  );
  const totalTokens = totalInputTokens + totalOutputTokens;
  const cacheReadTokens = sessions.reduce(
    (sum, s) => sum + s.cacheReadTokens,
    0,
  );

  // Cost
  const totalCost = sessions.reduce((sum, s) => sum + s.cost, 0);
  const tokensPerActiveHour = activeHours > 0 ? totalTokens / activeHours : 0;

  // Model mix
  const modelTokens = new Map<string, number>();
  for (const s of sessions) {
    const tokens = s.inputTokens + s.outputTokens;
    modelTokens.set(s.model, (modelTokens.get(s.model) ?? 0) + tokens);
  }
  const modelMix: Record<string, number> = {};
  for (const [model, tokens] of modelTokens) {
    modelMix[model] = totalTokens > 0 ? tokens / totalTokens : 0;
  }

  // Estimation quality
  const estimateAccuracy =
    data.estimatedTokens > 0 ? data.estimatedTokens / totalTokens : 0;

  const avgConfidence =
    sessions.reduce((sum, s) => sum + s.confidence, 0) / sessions.length;

  return {
    featureId: data.featureId,
    featureName: data.featureName,
    category: data.category,
    complexity: data.complexity,
    wallClockDays: Math.max(wallClockDays, 0),
    activeHours,
    sessionCount: sessions.length,
    totalInputTokens,
    totalOutputTokens,
    totalTokens,
    cacheReadTokens,
    totalCost,
    tokensPerActiveHour,
    modelMix,
    estimatedTokens: data.estimatedTokens,
    estimateAccuracy,
    autoTagConfidence: avgConfidence,
  };
}

/**
 * Calculate project-level velocity from per-feature velocities.
 */
export function calculateProjectVelocity(
  project: string,
  features: TrackedFeatureData[],
): ProjectVelocity {
  const featureVelocities = features.map(calculateFeatureVelocity);

  const completed = featureVelocities.filter(
    (f) => features.find((d) => d.featureId === f.featureId)?.status === "complete",
  );
  const inProgress = featureVelocities.filter(
    (f) => features.find((d) => d.featureId === f.featureId)?.status === "in-progress",
  );
  const withSessions = featureVelocities.filter((f) => f.sessionCount > 0);

  // Throughput: features completed per week
  let featuresPerWeek = 0;
  if (completed.length > 0) {
    const allStarts = completed.flatMap((f) =>
      f.wallClockDays > 0 ? [f.wallClockDays] : [],
    );
    const totalDays =
      allStarts.length > 0
        ? allStarts.reduce((sum, d) => sum + d, 0)
        : 1;
    featuresPerWeek = (completed.length / totalDays) * 7;
  }

  // Averages
  const avgCostPerFeature =
    withSessions.length > 0
      ? withSessions.reduce((sum, f) => sum + f.totalCost, 0) /
        withSessions.length
      : 0;
  const avgTokensPerFeature =
    withSessions.length > 0
      ? withSessions.reduce((sum, f) => sum + f.totalTokens, 0) /
        withSessions.length
      : 0;
  const avgSessionsPerFeature =
    withSessions.length > 0
      ? withSessions.reduce((sum, f) => sum + f.sessionCount, 0) /
        withSessions.length
      : 0;

  // Estimation accuracy
  const accuracies = withSessions
    .filter((f) => f.estimateAccuracy > 0)
    .map((f) => Math.abs(1 - f.estimateAccuracy));
  const overallAccuracy =
    accuracies.length > 0
      ? 1 - accuracies.reduce((sum, a) => sum + a, 0) / accuracies.length
      : 0;

  // Accuracy by complexity
  const accuracyByComplexity: Record<string, number> = {};
  const complexityGroups = new Map<string, number[]>();
  for (const f of withSessions) {
    if (f.estimateAccuracy <= 0) continue;
    const group = complexityGroups.get(f.complexity) ?? [];
    group.push(Math.abs(1 - f.estimateAccuracy));
    complexityGroups.set(f.complexity, group);
  }
  for (const [complexity, errors] of complexityGroups) {
    accuracyByComplexity[complexity] =
      1 - errors.reduce((sum, e) => sum + e, 0) / errors.length;
  }

  // Time averages
  const avgWallClockDays =
    withSessions.length > 0
      ? withSessions.reduce((sum, f) => sum + f.wallClockDays, 0) /
        withSessions.length
      : 0;
  const avgActiveHours =
    withSessions.length > 0
      ? withSessions.reduce((sum, f) => sum + f.activeHours, 0) /
        withSessions.length
      : 0;
  const totalActiveHours = withSessions.reduce(
    (sum, f) => sum + f.activeHours,
    0,
  );
  const totalWallClockHours = withSessions.reduce(
    (sum, f) => sum + f.wallClockDays * 24,
    0,
  );
  const activeToWallRatio =
    totalWallClockHours > 0 ? totalActiveHours / totalWallClockHours : 0;

  return {
    project,
    generatedAt: new Date().toISOString(),
    featuresCompleted: completed.length,
    featuresInProgress: inProgress.length,
    featuresTotal: features.length,
    featuresPerWeek,
    avgCostPerFeature,
    avgTokensPerFeature,
    avgSessionsPerFeature,
    overallAccuracy,
    accuracyByComplexity,
    avgWallClockDays,
    avgActiveHours,
    activeToWallRatio,
    totalCost: withSessions.reduce((sum, f) => sum + f.totalCost, 0),
    totalTokens: withSessions.reduce((sum, f) => sum + f.totalTokens, 0),
    features: featureVelocities,
  };
}

function emptyFeatureVelocity(data: TrackedFeatureData): FeatureVelocity {
  return {
    featureId: data.featureId,
    featureName: data.featureName,
    category: data.category,
    complexity: data.complexity,
    wallClockDays: 0,
    activeHours: 0,
    sessionCount: 0,
    totalInputTokens: 0,
    totalOutputTokens: 0,
    totalTokens: 0,
    cacheReadTokens: 0,
    totalCost: 0,
    tokensPerActiveHour: 0,
    modelMix: {},
    estimatedTokens: data.estimatedTokens,
    estimateAccuracy: 0,
    autoTagConfidence: 0,
  };
}
